import React, { useMemo, useState } from "react"
import Tabs from "aldoo-ra/Tabs"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import { MagnifyingGlassIcon, Bars3Icon } from "@heroicons/react/24/outline"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// The rail styles live here so the editors that use it stay identical.
const RAIL_TABS_OVERRIDES = {
  containerVertical: "flex flex-row h-auto w-full",
  tabListVertical: "flex flex-col w-full border-r border-admin_neutral_1 border-b-0 bg-admin_paper",
  tab: "relative w-full px-4 py-2.5 text-sm font-medium text-left justify-start transition-all duration-200 focus:outline-none flex items-center gap-2",
}

const SCROLLER = "overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper"

/**
 * The vertical tab rail of an editor whose model is composed: a filter on top,
 * because a model of many extensions has more tabs than the eye can scan.
 * The active tab always stays in the list, so filtering never hides where you
 * are.
 *
 * On a phone (below md) there is no room for a rail beside the fields: a bar
 * with a menu button and the current tab's name stands above them, and the
 * button opens the same list as a drawer sliding in from the left (the
 * admin_drawer_left modal, as the site's own mobile menu), with a close mark,
 * closed by a tap outside or Escape too. Picking a tab closes it. The labels
 * come translated in `tabs`; `texts` holds the rail's own words.
 * `helpKeyOf(tabId)`: the help center's key of a tab, shown as a "?" beside
 * its name (aldoo-ra/HelpCenter/InPlaceHelp); none for a tab it answers null.
 */
// the rail's own words; an editor in another language passes its translations
const TEXTS = { find: "Find a tab...", sections: "Sections" }

export default function TabRail({ tabs, value, onChange, className = "", texts = TEXTS, helpKeyOf = null }) {
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)

  const shown = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return tabs
    const matches = tabs.filter(
      (tab) => tab.value === value || String(tab.label).toLowerCase().includes(term)
    )
    return matches.length ? matches : tabs
  }, [tabs, query, value])

  const current = tabs.find((tab) => tab.value === value)
  // a tab's badge (its group's, a component) beside its name, and its help; the filter still reads the name
  const labelled = (list) =>
    list.map((tab) => {
      const helpKey = helpKeyOf ? helpKeyOf(tab.value) : null
      if (!tab.badge && !helpKey) return tab
      // the badge is a component the rail renders: the tab strip is not to draw one of its own
      const { badge: Badge, ...rest } = tab
      return {
        ...rest,
        label: (
          <span className="flex flex-1 min-w-0 items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <span className="truncate">{tab.label}</span>
              {helpKey && <HelpHint helpKey={helpKey} placement="bottom" align="start" />}
            </span>
            {Badge && <Badge tab={tab.value} />}
          </span>
        ),
      }
    })

  const pick = (next) => {
    onChange(next)
    setOpen(false)
  }

  const list = (
    <>
      <div className="p-2">
        <Input
          variant="admin"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={texts.find}
          className="w-full"
          icon={<MagnifyingGlassIcon className="h-4 w-4 text-admin_text_secondary" />}
          showClearButton={query.length > 0}
          onClear={() => setQuery("")}
        />
      </div>

      <div className={`flex-1 min-h-0 ${SCROLLER}`}>
        <Tabs
          tabs={labelled(shown)}
          value={value}
          onChange={pick}
          variant="admin"
          orientation="vertical"
          className="w-full"
          styleOverrides={{ ...RAIL_TABS_OVERRIDES, tabListVertical: "flex flex-col w-full border-b-0 bg-admin_paper" }}
        />
      </div>
    </>
  )

  return (
    <>
      {/* the phone's bar: the menu button and where you are */}
      <div className="md:hidden shrink-0 flex items-center gap-3 pb-3 mb-3 border-b border-admin_neutral_1" data-testid="tab-rail-bar">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-10 h-10 shrink-0 rounded-lg border border-admin_bordered flex items-center justify-center text-admin_text hover:bg-admin_neutral_1"
          aria-label={texts.sections}
          aria-expanded={open}
          data-testid="tab-rail-menu"
        >
          <Bars3Icon className="w-5 h-5" />
        </button>
        <span className="min-w-0 flex-1 flex items-center gap-2 text-sm font-semibold text-admin_text truncate">
          {current?.icon && <span className="shrink-0 [&>svg]:w-4 [&>svg]:h-4">{current.icon}</span>}
          <span className="truncate">{current?.label}</span>
        </span>
      </div>

      {/* the phone's drawer */}
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        variant="admin_drawer_left"
        title={texts.sections}
        closeOnClickOutside
        closeOnEscape
        styleOverrides={{
          overlay: "fixed top-0 left-0 w-full h-full flex justify-start bg-black/50 z-50 md:hidden",
          container: "w-[85vw] max-w-[360px] h-full flex flex-col bg-admin_paper shadow-2xl pb-[env(safe-area-inset-bottom)]",
          title: "flex-1 text-base font-bold text-admin_text",
          contentWrapper: "flex-1 min-h-0 flex flex-col overflow-hidden",
          content: "h-full min-h-0 flex flex-col",
        }}
      >
        <div className="h-full min-h-0 flex flex-col" data-testid="tab-rail-drawer">{list}</div>
      </Modal>

      {/* the rail, from md */}
      <div className={`hidden md:flex shrink-0 w-[260px] h-full min-h-0 flex-col border-r border-admin_neutral_1 ${className}`}>
        {list}
      </div>
    </>
  )
}
